import { useEffect, useRef } from "react";
import { createChart, CandlestickSeries } from "lightweight-charts";
import { chartTheme, CANDLE_COLORS } from "@/components/chartTheme";
import { cssHsl } from "@/components/TradeChart";
import { useTheme } from "@/context/ThemeContext";

// Small live candle chart with labelled horizontal price lines (Brain page: the setup level, its stop).
// The chart is created once per theme/height; new candles only update the data, so polling does not flicker
// and the user's zoom is kept. Axis times are New York time.
// lines: [{ price, title, tone: "brand" | "short" | "long" | "muted", dashed }]

const NY_FMT = new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit" });
const nyLabel = (t) => NY_FMT.format(new Date(t * 1000));

export default function LevelChart({ candles, lines = [], height = 260, fitKey }) {
  const ref = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const priceLinesRef = useRef([]);
  const extrasRef = useRef([]);
  const fittedRef = useRef(null);
  const { theme } = useTheme();
  const isDark = theme === "dark";

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const base = chartTheme(isDark);
    const chart = createChart(el, {
      ...base,
      localization: { ...base.localization, timeFormatter: nyLabel },
      timeScale: { ...base.timeScale, tickMarkFormatter: nyLabel },
      rightPriceScale: { ...base.rightPriceScale, scaleMargins: { top: 0.1, bottom: 0.1 } },
      handleScroll: { vertTouchDrag: false },   // let the page scroll on phones
      width: el.clientWidth,
      height,
    });
    const series = chart.addSeries(CandlestickSeries, {
      ...CANDLE_COLORS,
      priceFormat: { type: "price", precision: 2, minMove: 0.01 },
      priceLineVisible: false,
      // keep the level lines inside the visible price range
      autoscaleInfoProvider: (original) => {
        const r = original();
        const extras = extrasRef.current;
        if (!r || !extras.length) return r;
        return { ...r, priceRange: {
          minValue: Math.min(r.priceRange.minValue, ...extras),
          maxValue: Math.max(r.priceRange.maxValue, ...extras),
        } };
      },
    });
    chartRef.current = chart;
    seriesRef.current = series;
    priceLinesRef.current = [];
    fittedRef.current = null;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) if (e.contentRect.width > 0) chart.applyOptions({ width: e.contentRect.width });
    });
    ro.observe(el);
    return () => { ro.disconnect(); chart.remove(); chartRef.current = null; seriesRef.current = null; };
  }, [isDark, height]);

  useEffect(() => {
    const series = seriesRef.current;
    if (!series) return;
    series.setData(candles || []);
    if (candles?.length && fittedRef.current !== fitKey) {
      chartRef.current.timeScale().fitContent();
      fittedRef.current = fitKey;
    }
  }, [candles, fitKey, isDark, height]);

  const linesKey = JSON.stringify(lines);
  useEffect(() => {
    const series = seriesRef.current;
    const el = ref.current;
    if (!series || !el) return;
    priceLinesRef.current.forEach((pl) => { try { series.removePriceLine(pl); } catch (e) { /* gone */ } });
    const tones = {
      brand: cssHsl(el, "--ops-brand", "hsl(178, 84%, 40%)"),
      short: cssHsl(el, "--ops-short", "#ef4444"),
      long: cssHsl(el, "--ops-long", "#22c55e"),
      muted: cssHsl(el, "--ops-text-muted", "hsl(220, 10%, 70%)"),
    };
    const valid = lines.filter((l) => typeof l.price === "number" && isFinite(l.price));
    extrasRef.current = valid.map((l) => l.price);
    priceLinesRef.current = valid.map((l) => series.createPriceLine({
      price: l.price, color: tones[l.tone] || tones.brand, title: l.title, lineWidth: 1,
      lineStyle: l.dashed ? 2 : 0, axisLabelVisible: true,
    }));
    chartRef.current?.priceScale("right").applyOptions({});   // re-run autoscale with the new lines
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linesKey, isDark, height]);

  return <div ref={ref} data-testid="brain-level-chart" style={{ height }} />;
}
